/**
 * components/print/Checklist.js
 * Checklist Pengiriman Barang (PRD §5.6 — pusat only). Letterhead + `TANGGAL :` + title on the
 * left, buyer name + the contact's own Kota field top-right (NEVER parsed from an address —
 * Kontak already stores Kota separately), a `NO | QTY | PART NO. | PART NAME | COLY` table with
 * NO money columns and NO totals anywhere, `HAL:1/2`-style continuation with continuous row
 * numbering.
 *
 * **Phase T2c-2-fix-2 — letterhead trimmed to the company name only**: per the client's real
 * reference photo, this document's letterhead is JUST `company.name` — no address lines, no
 * NPWP (unlike Invoice Pusat's own letterhead, which keeps the full block; this document
 * doesn't read `company.addressLines`/`company.npwp` at all anymore).
 *
 * Portal-based, same pattern `InvoicePusat.js` established — `createPortal(...,
 * document.body)` so `#root` can be hidden with a real `display:none` in print, which is what
 * makes the named `@page` size honored at all.
 *
 * **Phase T2c-2-fix**: this document was originally built on `.print-doc--pusat` (the full
 * 9.5in×11in continuous form) under a wrong "pusat documents = full page" generalisation —
 * confirmed wrong via a real client screenshot: even a 15-real-row/0-filler-row page 1 (its
 * exact real 21-line sample) left a large blank gap below the table, proving the PAGE was too
 * tall, not that rows were missing. It now renders on `.print-doc--cabang` (the short
 * 9.5in×5.5in page). `ROWS_PER_PAGE_CHECKLIST` was re-verified by real measurement against the
 * corrected page's content height — see the value below and its own comment for whether the
 * original photo-derived `15` survived the correction or needed to change.
 *
 * **The cabang @page geometry fix**: `.print-doc--cabang` now sits inside a page declared at
 * the SAME 8.5in×11in geometry as `.print-doc--pusat` — a real physical print of Surat Jalan
 * Pusat overflowed onto a second sheet on the real EPSON LX-300+II, traced to the driver
 * picking the wrong paper preset for a landscape-shaped `8.5in×5.5in` page request; this
 * document shares the exact same `@page cabang` rule and is fixed by the same change. This
 * document's OWN layout/rows/`ROWS_PER_PAGE_CHECKLIST` are UNCHANGED — see
 * `printFoundation.css`'s own header comment (last dated paragraph) for the full investigation.
 */
import React from "react";
import { createPortal } from "react-dom";
import "./printFoundation.css";
import { paginateLines, NBSP, PRINT_SETTINGS_REMINDER_CABANG } from "../../utils/pagination";
import { fmtQty, fmtDateLong } from "../../utils/idGenerators";
import { displayContact } from "../../utils/contacts";

// **DISCREPANCY, reported per the task's own instruction rather than silently kept**: the
// original `15` was derived from a real client photo — but that photo was of a Checklist
// printed on the WRONG (tall, pusat-sized) page, which is exactly the bug this session fixes.
// Measured fresh via a real headless-Chrome print-media render against the CORRECTED 5.0in
// content height (Phase T2c-2-fix): 422.4px inner content height − 138.98px header/hr overhead
// (no footer on this document) − 26.98px thead row, leaving 256.43px for data rows at a real
// measured row height of 26.98px → floor(256.43 / 26.98) = 9, not 15. Confirmed via page.pdf():
// 9 rows render on one real physical page with no overflow; 10 rows genuinely split onto a
// second page — the real 21-line client sample now splits 9/9/3 across three pages instead of
// the old (wrong-page) 15/6 across two; see Checklist.test.js's updated expectations.
//
// Print-width foundational correction -- RE-VERIFIED, CONFIRMED UNCHANGED at 9. The Part Name
// column shrank from 4.3in to 3.8in once .print-doc--cabang's content width was corrected
// 8.7in to 8.2in (see printFoundation.css's own header comment). Real page.pdf() re-measurement
// -- 9 rows including one genuinely long real Part Name (69 characters, wrapping to 2 lines) --
// still renders on exactly one real physical page with no overflow. This document already
// carries 38px of deliberately unused header slack (the T2c-2-fix-2 letterhead trim, flagged at
// the time as a follow-up opportunity never acted on) -- that unused slack is exactly what
// absorbed the new wrap-cost regression here for free, unlike the two Surat Jalan documents.
//
// **The row-budget slack investigation — this IS that flagged follow-up, now acted on: `9` ->
// `10`.** Real physical prints showed visible unused space below the table even at `9`. Unlike
// Invoice Cabang, this is honestly a forward-looking buffer, not protection against a real
// current threat: no name in the real 1,179-item catalog wraps at this column's real 3.8in
// width at all (confirmed — even the real 35-character catalog max renders on one line here).
// A real `page.pdf()`/`getBoundingClientRect()` measurement (temporary `puppeteer-core`+
// `pdf-lib`, installed `--no-save`, removed after) used a synthetic 76-character probe (no real
// catalog counterpart) purely to establish a worst-case margin, since no real name currently
// wraps to measure against: at `N=10`, that synthetic worst case still lands at **32.4px of
// real margin** (`11` was also measured and rejected — a razor-thin 5.4px, the same class of
// mistake this project's own history already learned to avoid). `10` uses roughly one more row
// of the header slack already flagged as available, as a buffer against item names growing
// longer over time. **Still pending physical confirmation on the real EPSON LX-300+II.**
const ROWS_PER_PAGE_CHECKLIST = 10;

export default function Checklist({ txn, items = [], contact, branches = [], company, onClose }) {
  const byCode = new Map(items.map((it) => [it.internalCode, it]));
  const { pages, pageCount } = paginateLines(txn.lines || [], ROWS_PER_PAGE_CHECKLIST);
  const multiPage = pageCount > 1;
  // Contact model redesign leak fix: a TRANSFER's `contact` is always an app-maintained internal
  // stand-in whose `.name` is a fixed, never-real placeholder ("__internal__") — reading it
  // directly would print that literal string as the buyer name. `displayContact` resolves the
  // real branch name instead (or "" when it genuinely can't — never the raw field).
  const contactName = contact ? displayContact(contact, branches) : "";

  return createPortal(
    <div className="print-doc-overlay">
      <div className="print-doc-overlay__actions">
        <span className="print-doc-overlay__note">Checklist — {txn.invoiceNumber || txn.id}</span>
        <span className="print-doc-overlay__reminder">{PRINT_SETTINGS_REMINDER_CABANG}</span>
        <button type="button" className="btn" onClick={onClose}>Tutup</button>
        <button type="button" className="btn btn-primary" onClick={() => window.print()}>Cetak</button>
      </div>

      <div className="print-doc print-doc--cabang">
        {pages.map((page) => {
          const fillerCount = Math.max(0, ROWS_PER_PAGE_CHECKLIST - page.lines.length);
          return (
            <div className="print-page" key={page.pageNumber}>
              <div className="print-doc__header">
                <div className="print-doc__header-col">
                  <div className="print-doc__title">{company.name}</div>
                  <div className="print-doc__header-invoice">
                    <span className="print-doc__label">TANGGAL : </span>{fmtDateLong(txn.date)}
                  </div>
                  <div className="print-doc__header-invoice">
                    <b>CHECKLIST PENGIRIMAN BARANG</b>
                    {multiPage && <span className="print-doc__hal" style={{ marginLeft: 12 }}>HAL:{page.pageNumber}/{pageCount}</span>}
                  </div>
                </div>
                <div className="print-doc__header-col print-doc__header-col--right">
                  <div>{contactName}</div>
                  <div>{contact?.city}</div>
                </div>
              </div>

              <hr className="print-doc__hr" />

              <div className="print-doc__table-area">
                <table className="print-doc__table">
                  <colgroup>
                    <col style={{ width: "0.4in" }} />
                    <col style={{ width: "0.9in" }} />
                    <col style={{ width: "1.3in" }} />
                    <col />
                    <col style={{ width: "1.0in" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>No</th>
                      <th className="num">Qty</th>
                      <th>Part No.</th>
                      <th>Part Name</th>
                      <th>Coly</th>
                    </tr>
                  </thead>
                  <tbody>
                    {page.lines.map((l, i) => {
                      const rowNo = (page.pageNumber - 1) * ROWS_PER_PAGE_CHECKLIST + i + 1;
                      const name = l.name || byCode.get(l.internalCode)?.name || l.internalCode;
                      return (
                        <tr key={i}>
                          <td>{rowNo}</td>
                          <td className="num">{fmtQty(l.qty)} PCS</td>
                          <td>{l.internalCode}</td>
                          <td className="wrap">{name}</td>
                          <td>{l.collyNo || ""}</td>
                        </tr>
                      );
                    })}
                    {Array.from({ length: fillerCount }).map((_, i) => (
                      <tr className="print-doc__filler" key={`filler-${i}`}>
                        <td>{NBSP}</td><td>{NBSP}</td><td>{NBSP}</td><td>{NBSP}</td><td>{NBSP}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    </div>,
    document.body
  );
}
